import React from 'react'

import List from '@mui/material/List'
import Stack from '@mui/material/Stack'

import { useAccountStore, useGlobalCopyStore, useGoalStore, useAppConfig } from '../../common'
import { type Account, type Goal, AccountType, GoalType, MetaType, TrackType } from '../../core'
import getAccountDisplayName from '../../common/utils/AccountDisplayName'

import { getGoalTypes } from '../../core/utils/GoalsUtil'
import { AddGoalListItem } from './addgoal/AddGoalListItem'
import { AccountSelectionDrawer } from './AccountSelectionDrawer'

interface AddGoalProps {
  onClose?: (trackType: TrackType) => void
}

export const AddGoal = ({ onClose }: AddGoalProps) => {
  const { goals: copy } = useGlobalCopyStore()
  const { addGoal, debtGoals, setAlert, setSelectedGoal } = useGoalStore()
  const { config } = useAppConfig()
  const { visibleDebtAccounts: debtAccounts } = useAccountStore()

  const [isAccountsOpen, setIsAccountsOpen] = React.useState(false)

  const goalTypes = getGoalTypes(copy)

  const handleClick = (name: string, metaType: MetaType, type: TrackType) => {
    if (type === TrackType.DEBT_TRACK) {
      setIsAccountsOpen(true)
    } else {
      setSelectedGoal({
        amount: 0,
        current_amount: 0,
        goal_type: GoalType.SAVE_AMOUNT,
        initial_amount: 0,
        meta_type: metaType,
        name: name,
        track_type: type,
      } as Goal)
    }
  }

  const handleSaveDebtAccounts = async (accounts: Account[]) => {
    for (const account of accounts) {
      let metaType = MetaType.CREDIT_CARD
      if (account.account_type === AccountType.LOAN) {
        metaType = MetaType.LOANS
      } else if (account.account_type === AccountType.LINE_OF_CREDIT) {
        metaType = MetaType.LINE_OF_CREDIT
      }

      await addGoal({
        account_guid: account.guid,
        amount: 0,
        current_amount: 0,
        goal_type: GoalType.KEEP_BALANCE_BELOW,
        initial_amount: account.balance || 0,
        interest_rate: account.interest_rate || 0,
        meta_type: metaType,
        monthly_payment: account.minimum_payment || 0,
        name: getAccountDisplayName(account, {
          allowNickname: config?.show_account_nicknames_in_master,
        }),
        track_type: TrackType.DEBT_TRACK,
      } as Goal)
      setAlert(copy.alert_goal_added)
    }
    setIsAccountsOpen(false)
    onClose?.(TrackType.DEBT_TRACK)
  }

  return (
    <Stack>
      <List sx={{ bgcolor: 'background.paper' }}>
        {goalTypes.map((goalType, index) => (
          <AddGoalListItem goalType={goalType} key={index} onClick={handleClick} />
        ))}
      </List>

      <AccountSelectionDrawer
        accounts={debtAccounts.filter((a) => !debtGoals.find((g) => g.account_guid === a.guid))}
        isOpen={isAccountsOpen}
        onClose={() => setIsAccountsOpen(false)}
        onSave={handleSaveDebtAccounts}
        title={copy.select_debt_accounts_title}
      />
    </Stack>
  )
}
